import { useLazyQuery } from "@apollo/client";
import {
  Alert,
  Chip,
  ChipProps,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { useCallback } from "react";

const BOLDSIGN_DOCUMENT_INFO_QUERY = gql(`
  query GetBoldSignDocumentInfo($submissionId: ID!) {
    getBoldSignDocumentInfo(submissionId: $submissionId) {
      documentId
      status
      title
      createdDate
      expiryDate
      signers {
        name
        email
        status
        order
      }
      formFields {
        id
        type
        pageNumber
        isRequired
        value
        signerEmail
        bounds {
          x
          y
          width
          height
        }
      }
    }
  }
`);

function formatBoldSignTimestamp(ts: number | null | undefined): string {
  if (ts == null) return "—";
  return new Date(ts * 1000).toLocaleString();
}

function formatStatusLabel(status: string | null | undefined): string {
  if (!status) return "Unknown";
  return status.replace(/([A-Z])/g, " $1").trim();
}

function getStatusChipProps(status: string | null | undefined): ChipProps {
  const s = (status ?? "").toLowerCase();
  if (s === "completed" || s === "signed") return { color: "success" };
  if (s === "inprogress" || s === "sent") return { color: "info" };
  if (s === "notcompleted" || s === "draft") return { color: "default" };
  if (s === "expired" || s === "declined" || s === "revoked") return { color: "error" };
  return { color: "default" };
}

const FORM_FIELD_TYPE_LABELS: Record<string, string> = {
  initial: "Initial",
  signature: "Signature",
  datepicker: "Date",
  date: "Date",
  text: "Text",
  checkbox: "Checkbox",
  radio: "Radio",
};

function formatFormFieldType(type: string | null | undefined): string {
  if (!type) return "—";
  const lower = type.toLowerCase();
  return FORM_FIELD_TYPE_LABELS[lower] ?? type;
}

interface BoldSignDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  submissionId: string;
}

export function BoldSignDetailsDialog({ open, onClose, submissionId }: BoldSignDetailsDialogProps) {
  const [fetchInfo, { data, loading, error, called }] = useLazyQuery(BOLDSIGN_DOCUMENT_INFO_QUERY, {
    fetchPolicy: "network-only",
  });

  const handleOpen = useCallback(() => {
    fetchInfo({ variables: { submissionId } });
  }, [fetchInfo, submissionId]);

  if (open && !called) {
    handleOpen();
  }

  const info = data?.getBoldSignDocumentInfo;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      title="BoldSign Document Details"
      noPrimaryAction
      hideCancelButton
      contentSx={{ pb: 3 }}
    >
      {loading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      ) : error ? (
        <Alert severity="error">Failed to load BoldSign document info: {error.message}</Alert>
      ) : !info ? (
        <Alert severity="info">No BoldSign document found for this submission.</Alert>
      ) : (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Document
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                <Typography variant="body2" component="span">
                  <strong>Status:</strong>{" "}
                </Typography>
                <Chip
                  label={formatStatusLabel(info.status)}
                  size="small"
                  {...getStatusChipProps(info.status)}
                />
              </Stack>
              <Typography variant="body2">
                <strong>Title:</strong> {info.title ?? "—"}
              </Typography>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Typography variant="body2">
                  <strong>Created:</strong> {formatBoldSignTimestamp(info.createdDate)}
                </Typography>
                <Typography variant="body2">
                  <strong>Expiry:</strong> {formatBoldSignTimestamp(info.expiryDate)}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          {info.signers.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Signers
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {info.signers.map((signer, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{signer.order ?? "—"}</TableCell>
                        <TableCell>{signer.name ?? "—"}</TableCell>
                        <TableCell>{signer.email ?? "—"}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatStatusLabel(signer.status)}
                            size="small"
                            {...getStatusChipProps(signer.status)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}

          {info.formFields.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Form Fields ({info.formFields.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Page</TableCell>
                      <TableCell>Position (x, y)</TableCell>
                      <TableCell>Size (w x h)</TableCell>
                      <TableCell>Required</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...info.formFields]
                      .sort((a, b) => (a.pageNumber ?? 0) - (b.pageNumber ?? 0))
                      .map((field, idx) => (
                        <TableRow key={field.id ?? idx}>
                          <TableCell>{field.id ?? "—"}</TableCell>
                          <TableCell>{formatFormFieldType(field.type)}</TableCell>
                          <TableCell>{field.pageNumber ?? "—"}</TableCell>
                          <TableCell>
                            {field.bounds
                              ? `(${field.bounds.x?.toFixed(0)}, ${field.bounds.y?.toFixed(0)})`
                              : "—"}
                          </TableCell>
                          <TableCell>
                            {field.bounds
                              ? `${field.bounds.width?.toFixed(0)} x ${field.bounds.height?.toFixed(0)}`
                              : "—"}
                          </TableCell>
                          <TableCell>{field.isRequired ? "Yes" : "No"}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </Stack>
      )}
    </Dialog>
  );
}
